<template>
  <div class="card-edit card-theme card-edit-new-theme"  :key="refreshDetail" v-on="dirtyListeners">
    <card-edit-subheader
      v-if="cardLoaded"
      :type="type"
      :stage="card.stage"
      :card="card"
      :status="card.status"
      >
    </card-edit-subheader>
    <div class="edit-body has-background-grey-lighter">
      <div class="" v-if="cardLoaded">
        <div class="column primary-card-info">
          <div class="columns">
            <div class="column is-flex">
              <i class="has-text-white image is-48x48 is-inline-block"
              :class="getDynamicClasses"> </i>
              <div class="has-text-dark is-flex is-justify-content-flex-end">
                <span>{{ card.name }}</span>
              </div>
            </div>
            <div class="column is-2 px-0">
              <div class="has-text-dark">
                 <label class="has-text-black-bis is-italic is-size-3 label">Order Type</label>
                <span>Shipping</span>
              </div>
            </div>
          </div>
        </div>
        <div class="column secondary-card-info">
          <div class="columns is-multiline">
            <div class="column is-narrow">
              <field-stage
                class="is-flex"
                label="Overall Shipping Status:"
                :isEditing="true"
                :shipType="card.shipType"
                :value="card.status"
                :shippingItems="card.items"
                :isStatus="true"
              ></field-stage>
            </div>
            <div class="column"></div>
            <div class="column">
              <div class="field is-flex is-justify-content-space-between">
                <span class="has-text-black-bis is-italic is-size-3 has-text-weight-semibold">
                  Total # Deliveries:
                </span>
                <span class="is-size-2 has-text-weight-bold">
                  {{ card?.partialShipments?.filter((partial) => !['unscheduled', 'underDelivered'].includes(partial.id)).length }}
                </span>
              </div>
            </div>
             <div class="column">
              <div class="field is-flex is-justify-content-space-between">
                <span class="has-text-black-bis is-italic is-size-3 has-text-weight-semibold">
                  Total # Items:
                </span>
                <span class="is-size-2 has-text-weight-bold">
                  {{ card?.items?.length }}
                </span>
              </div>
            </div>
            <div class="column has-text-dark  is-2">
                 <span class="has-text-black-bis is-italic is-size-3 has-text-weight-semibold">Earliest Onsite:</span>
                <field-date
                  class="is-pulled-right"
                  :inputProps="{ kind: 'minItemDelivery', disableEdit: true }"
                  :isEdit="false"
                  :rowData="card"
                  :rowField="{ prop: 'deliver' }"/>
            </div>
            <div class="column has-text-dark  is-2">
              <div>
                 <span class="has-text-black-bis is-italic is-size-3 has-text-weight-semibold">Earliest Ship By:</span>
                <field-date
                  class="is-pulled-right"
                  :inputProps="{ kind: '_delivery.deliveryStart', disableEdit: true }"
                  :isEdit="false"
                  :rowData="card"
                  :rowField="{ prop: 'shipBy' }"/>
              </div>
            </div>
            <div class="column has-text-dark is-2">
              <div>
                <span class="is-italic has-text-weight-semibold has-text-black-bis is-italic is-size-3">Latest Delivery By:</span>
                <field-date
                  class="is-pulled-right"
                  :inputProps="{ kind: '_delivery.deliverBy', disableEdit: true }"
                  :isEdit="false"
                  :rowData="card"
                  :rowField="{ prop: 'latestDeliverBy' }"/>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-edit-item-table has-background-grey-lighter">
        <mf-detail-row v-if="cardLoaded"
          :rowData="card"
          :stage="card.stage"
          :refreshCard="loadCard"
          @tabName="setTabName"
          @saveCard="saveCard"
          :tabLabel="selectedTabName"
          :customFieldsCols="cfColumns"
          >
        </mf-detail-row>
      </div>
    </div>
    <o-modal
      v-if="isItemsDeliveryActive"
      :active="isItemsDeliveryActive"
      :canCancel="false"
      :trapFocus="true"
      class="modal-sm"
    >
      <item-deliveries
        :card="card"
        :rowData="isItemsDeliveryRow"
        @close="closeDeliveryItems"
      />
    </o-modal>
    <o-modal
      v-if="isSourceOrderActive"
      :active="isSourceOrderActive"
      :canCancel="false"
      :trapFocus="true"
      class="modal-sm"
    >
      <source-order
        :card="card"
        :project="card.project"
        :rowData="isSourceOrderRow"
        @close="toggleSrcOrder"
      />
    </o-modal>
    <o-modal
      v-if="isDeliveryItemsActive"
      :active="isDeliveryItemsActive"
      :canCancel="false"
      :trapFocus="true"
      class="modal-sm"
    >
      <delivery-items
        :key="refreshKey"
        :rowData="isDeliveryItemsRow"
        :isUnscheduledRow="isUnscheduledRow"
        @close="closeDeliveryItems"
        :refreshCard="discardChanges"
        :masterCard="card"
      />
    </o-modal>
    <Notes v-if="notesEdit"
      isIconField
      :order="selectedOrderFor.notes"
      :isActive="notesEdit"
      notesType="deliveryOrd"
      @close="closeNotesModal"
      @update:value="updateNotes">
    </Notes>
    <Documents v-if="docsEdit"
      :isActive="docsEdit"
      :rowData="selectedOrderFor.docs"
      @close="closeDocsModal"
      @saveDocs="saveDocs">
    </Documents>
    <o-loading :active="isLoading" />
  </div>
</template>

<script>
import {
  reactive, toRefs, onBeforeMount, computed, inject, provide,
onBeforeUnmount,
} from 'vue'
import { isEmpty, set } from 'lodash';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { ModalProgrammatic } from '@oruga-ui/oruga-next';

// Below are local file imports
import Shipping from '@/models/Shipping';
import Notes from '@/components/OrderNotes.vue';
import SourceOrder from '@/components/modals/SourceOrder.vue'
import CardEditMixin from '@/components/mixins/CardEditMixin';
import Documents from '@/components/modals/TableDocuments.vue';
import DeliveryItems from '@/components/modals/DeliveryItems.vue';
import MfDetailRow from '@/components/table-fields/MfDetailRow.vue';
import ItemDeliveries from '@/components/modals/ItemDeliveries.vue';
import GeneralShippingMixin from '@/components/mixins/GeneralShippingMixin';
import CardEditSubHeader from '@/components/card-edit/CardEditSubheader.vue';
import ShippingItemsTab from '@/components/table-fields/ShippingItemsTab.vue';
import CreateDeliveries from '@/components/modals/CreateDeliveries.vue';
import CustomFields from '@/models/CustomFields';
import { customFieldColsConfig } from '@/components/mixins/UtilityMixin';

export default {
  name: 'shipping-card-view',
  components: {
    'card-edit-subheader': CardEditSubHeader,
    'mf-detail-row': MfDetailRow,
    'shipping-items-tab': ShippingItemsTab,
    'item-deliveries': ItemDeliveries,
    'source-order': SourceOrder,
    DeliveryItems,
    Notes,
    Documents,
    CreateDeliveries
    
  },
  props: {
    projectId: {
      type: String,
      required: true,
    },
    cardId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { onInitDirty } = CardEditMixin();
    const { updateShipment, deleteShipment } = GeneralShippingMixin();
    const router = useRouter();
    const store = useStore();
    const toast = useToast();
    const emitter = inject('emitter');

    const data = reactive({
      type: '',
      card: {},
      rowField: {}, // used for item document slider
      // itemsData: [],
      docsEdit: false,
      notesEdit: false,
      selectedItem: {}, // used for notes and docs slider
      refreshDetail: 0,
      isLoading: false,
      cardLoaded: false,
      checkListOrder: {}, // used for item document slider
      selectedOrderFor: {
        notes: {},
        docs: {},
      },
      selectedTabName: 'deliveries',
      isSourceOrderRow: {},
      isItemsDeliveryRow: {},
      isDeliveryItemsRow: {},
      isNotesDisabled: false,
      isUnscheduledRow: false,
      deliveryProjectsList: [],
      isFileListVisible: false,
      isSlideNotesActive: false,
      isSourceOrderActive: false,
      isDeliveryItemsActive:false, // used for delivery items modal
      isItemsDeliveryActive: false,
      refreshKey: 0,
      cfColumns: [],
      // isItem: false,
    });

    onInitDirty(data);

    const loadCard = async () => {
      try {
        data.isLoading = true;
        onInitDirty(data);
        data.type = store.state.activeScreen;
        const order = await Shipping.getPartialShipments({
          shippingLabelId: props.cardId,
        });
        data.card = order;
        data.refreshDetail += 1;
      } catch(e) {
        console.log('Error: ', e);
      } finally {
        data.isLoading = false;
      }
    }

    const getDynamicClasses = computed(() => {
      const routeMeta = router.currentRoute.value.meta;
      let classes = '';
      if (routeMeta.activeViewColor) {
        classes = `${routeMeta.activeViewColor} `;
      }
      if (routeMeta.cardEditIcon) {
        classes += `icon-${routeMeta.cardEditIcon}`;
      }
      return classes;
    });

    const setTabName = (val) => {
      data.selectedTabName = val;
    };

    const saveCard = () => {}

    const closeDeliveryItems = async (params) => {
      data.isDeliveryItemsActive = false;
      data.isItemsDeliveryActive = false;
      if (params?.doRefresh) {
        await discardChanges();
      }
    };

    const toggleSrcOrder = async (params) => {
      data.isSourceOrderActive = !data.isSourceOrderActive;
      if (params?.doRefresh) {
        data.refreshDetail++;
      }
    };

    emitter.on('toggle:isItemsDelivery', (payload) => {
      data.isItemsDeliveryRow = payload.rowData;
      data.isItemsDeliveryActive = true;
    });

    emitter.on('toggle:isSourceOrder', (payload) => {
      data.isSourceOrderRow = payload.rowData;
      data.isSourceOrderActive = true;
    });

    onBeforeMount(async () => {
      localStorage.removeItem('parentShipment');
      await loadCard();
      if (store.state.activeScreen === 'shipping-card-edit' && data.selectedTabName === 'deliveries') {
        const delCustomFields = await CustomFields.getCustomFields({ type: 'sl_delivery' });
        if (!isEmpty(delCustomFields) && !isEmpty(delCustomFields.fields)) {
            delCustomFields.fields.forEach((cf) => {
              const props = customFieldColsConfig[cf.__t];
              if (props) {
                data.cfColumns.push({
                  name: props.name,
                  width: props.width,
                  title: cf.label,
                  customField: cf,
                  emitOrderSave: true,
                  isEditable: true,
                  customHeader: true,
                });
              }
            });
          }
      }
      data.cardLoaded = true;
    });

    onBeforeUnmount(() => {
      emitter.off('toggle:isItemsDelivery');
      emitter.off('toggle:isSourceOrder');
      emitter.off('toggle:isDeliveryItems');
      emitter.off('toggle:createDelModal');
    })

    const saveDetails = async () => {
      data.isLoading = true;
        if (data.card?.shipType === 's-m' && ['in-storage', 'released-to-inventory', 'zombie'].includes(data.card.status)) {
          data.card.partialShipment = [];
          await data.card.updateShippingDetails();
        } else {
          const shippingOrder = _.cloneDeep(data.card);
          // Not sure about below condition please un-comment
          // if the items save breaking
          // if (data.itemsData.length) {
          //   shippingOrder.items = _.cloneDeep(data.itemsData);
          // }
          try {
            await updateShipment(shippingOrder);
          } catch (err) {
            throw new Error(`Shipment update failed ${err}`);
          }
        }
        await loadCard();
        data.refreshDetail++;
        data.isLoading = false;
     }

    const discardChanges = async (params) => {
      await loadCard();
      if (params && params.id) data.isDeliveryItemsRow = _.find(data.card.partialShipments, (partial) => partial._id === params.id)
      data.refreshKey++;
    }

    const deleteShipOrder = async () => {
      data.isLoading = true;
      const refresh = false;
      await deleteShipment(data.card, refresh, data);
      data.isLoading = false;
    }

    emitter.on('toggle:isDeliveryItems', (payload) => {
      data.isDeliveryItemsRow = payload.rowData;
      data.isUnscheduledRow = payload.isUnscheduledRow;
      data.isDeliveryItemsActive = true;
    });

    provide('shippingActionEvents', {
      save: saveDetails,
      discardChanges,
      delete: deleteShipOrder,
    })

    provide('notesModalActions', {
      openModal(selectedDelivery) {
        data.notesEdit = true;
        data.selectedOrderFor.notes = selectedDelivery;
      },
    })

    provide('docsModalActions', {
      openModal(selectedDelivery) {
        data.docsEdit = true;
        data.selectedOrderFor.docs = selectedDelivery;
      },
    })

    const closeNotesModal = () => {
      data.notesEdit = !data.notesEdit;
      data.selectedOrderFor.notes = {}
    }

    const updateShippingLabel = async (shippinglabel) => {
      try {
        data.isLoading = true;
        await updateShipment(shippinglabel, false);
      } catch (e) {
        console.log('Error: ', e)
      } finally {
        data.isLoading = false;
      }
      await loadCard();
    };

    const updateNotes = async (notes) => {
      set(data.selectedOrderFor.notes, 'delivery.notes', notes);
      await updateShippingLabel(data.selectedOrderFor.notes)
    }

    const closeDocsModal = () => {
      data.docsEdit = !data.docsEdit;
      data.selectedOrderFor.docs = {}
    }

    const saveDocs = async () => {
      await updateShippingLabel(data.selectedOrderFor.docs, false)
    }

    emitter.on('toggle:createDelModal', async (payload) => {
      data.isDeliveryItemsActive = false;
      ModalProgrammatic.open({
        component: CreateDeliveries,
        props: {
          card: payload.card,
          masterShipment: payload.masterShipment,
        },
        trapFocus: true,
        canCancel: false,
        rootClass: 'modal-sm',
        events: {
          close: async() => {
            await discardChanges();
          },
        },
      });
    });

    return {
      ...toRefs(data),
      getDynamicClasses,
      setTabName,
      saveCard,
      closeDeliveryItems,
      toggleSrcOrder,
      loadCard,
      closeNotesModal,
      updateNotes,
      closeDocsModal,
      saveDocs,
      discardChanges,
    }
  }
}
</script>

<style>

</style>